import React from "react";
import {
  Alert,
  Box,
  ExpandableSection,
  HelpPanel,
  Link,
} from "@amzn/awsui-components-react";
import { ExternalLinkGroup } from "../../commons";
import { learnMoreLinks } from "src/pages/commons/learn-more-links";
import { useJemGen } from "src/context/jem-gen/jemGenContext";
import { CodeViewWithCopy } from "./code-view-with-copy";
import {
  Ard,
  TemplateType,
  UseCaseName,
} from "@amzn/fuse-accounting-central-datastore-client";

export function JemGenToolsContent() {
  const { toolsContentSelection } = useJemGen();

  if (toolsContentSelection === "AidedFlareAuthoring") {
    return <AidedFlareAuthoring />;
  }

  return (
    <HelpPanel
      header={<h2>Jem Gen</h2>}
      footer={<ExternalLinkGroup items={learnMoreLinks} />}
    >
      <p>ARD Creation Tool</p>
    </HelpPanel>
  );
}

const FF_INSTALL_CMD = `(rm -rf ~/.fuse-flare/FuseFlareCLI \
&& mkdir -p ~/.fuse-flare && cd ~/.fuse-flare \
&& brazil workspace create -n FuseFlareCLI -vs FuseAccountingCentralDatastore/development -p FuseFlareCLI \
&& cd FuseFlareCLI/src/FuseFlareCLI \
&& brazil-build \
&& brazil-build install-cli
)`;

const FF_UPDATE_CMD = `[[ -d ~/.fuse-flare/FuseFlareCLI/src/FuseFlareCLI ]] \
&& (cd ~/.fuse-flare/FuseFlareCLI/src/FuseFlareCLI && git pull --rebase && brazil-build) \
|| (echo "\nError: FuseFlareCLI is not installed correctly." && exit 1)
`;

export function AidedFlareAuthoring() {
  const { selectedArd, ardRegionMap } = useJemGen();

  const ardId = selectedArd?.id;
  const ardDomain = (() => {
    const domain = ardRegionMap.get(ardId || "");
    switch (domain) {
      case "prod-US":
        return "prod-NA";
      case "devo-FE":
        return "beta";
      default:
        return domain;
    }
  })();

  const ffPrepareArgs = [
    "--ard-id",
    `${ardId}`,
    "--fd",
    `${ardDomain}`,
    "--verbose",
    "--dry-run-release",
  ];
  if (ardDomain !== "beta") {
    ffPrepareArgs.push("--no-devo-integ");
  }

  const steps = [
    {
      title: "Authenticate with Midway",
      content: <CodeViewWithCopy content="mwinit -s -o" />,
    },
    {
      title: "Clear flare-authoring workspace and initialize Flare env",
      content: <CodeViewWithCopy content="ff clear && ff init" />,
    },
    {
      title: "[Optional] Reuse an existing work request",
      content: (
        <CodeViewWithCopy content="ff use-work-request <work-request-id>" />
      ),
    },
    {
      title: "Prepare work request",
      content: (
        <>
          <p>Run the following command:</p>
          <CodeViewWithCopy content={`ff prepare ${ffPrepareArgs.join(" ")}`} />
          {selectedArd && selectedArd?.template !== TemplateType.STANDARD && (
            <React.Fragment>
              <p>
                Since this is a non-standard ARD, we need to manually update the
                carve_split_name mapping at:
              </p>
              <CodeViewWithCopy content="~/.fuse-flare/flare-authoring/filesystem/global/named_mapping/named_table_mapping_element_group.fuse_carve_split_name_global_map.ion" />
              <p>
                Also be sure to add the processing test before creating the
                release.
              </p>
            </React.Fragment>
          )}
          {selectedArd && selectedArd?.template === TemplateType.STANDARD && (
            <React.Fragment>
              <p>
                After the above command succeeds, you should get a link to a
                dry-run devo release. The release will also trigger the
                processing test. If the release fails, please check the error
                and fix the Flare config accordingly. You can find the Flare
                config at:
              </p>
              <CodeViewWithCopy content="~/.fuse-flare/flare-authoring/" />
            </React.Fragment>
          )}
        </>
      ),
    },
    {
      title: "Initiate a continuous release",
      content: (
        <>
          <p>Run the following command to release the changes up to uat:</p>
          <CodeViewWithCopy content="ff continuous-release" />
        </>
      ),
    },
  ];

  return (
    <HelpPanel header={<h2>Aided Flare Authoring</h2>}>
      <ExpandableSection headerText="Prerequisites">
        <ol>
          <li>
            Make sure you have <Box variant="awsui-inline-code">Flare CLI</Box>{" "}
            installed
          </li>{" "}
          per{" "}
          <Link
            href="https://console.harmony.a2z.com/flare-docs/users/flare-CLI/introduction#install-flarecli-to-your-devbox-or-mac-laptop"
            external
          >
            Install FlareCLI
          </Link>
          <li>
            Make sure you have <Box variant="awsui-inline-code">node</Box>{" "}
            installed. To install, run
            <CodeViewWithCopy
              content={"toolbox install axe\naxe init builder-tools"}
            />
          </li>
          <li>It is recommended that you run this on an AL2 cloud desktop</li>
          <li>
            You should be a member of{" "}
            <Link
              href="https://permissions.amazon.com/a/team/FLASH%20Tenant%20Flare%20Users"
              external
            >
              FLASH Tenant Flare Users
            </Link>
          </li>
        </ol>
      </ExpandableSection>
      <ExpandableSection headerText="Installing Fuse Flare CLI">
        <p>
          You can install Fuse Flare CLI by running the following command after{" "}
          <Box variant="awsui-inline-code">mwinit -s -o</Box>:
        </p>
        <CodeViewWithCopy content={FF_INSTALL_CMD} />
        <p>
          Please restart your terminal or run{" "}
          <Box variant="awsui-inline-code">source ~/.zshrc</Box> or{" "}
          <Box variant="awsui-inline-code">source ~/.bashrc</Box> before
          proceeding.
        </p>
      </ExpandableSection>
      <ExpandableSection headerText="Updating Fuse Flare CLI">
        <p>
          If you had installed Fuse Flare CLI before, you can update Fuse Flare
          CLI by running the following command after{" "}
          <Box variant="awsui-inline-code">mwinit -s -o</Box>:
        </p>
        <CodeViewWithCopy content={FF_UPDATE_CMD} />
      </ExpandableSection>
      <ExpandableSection
        defaultExpanded
        headerText="Preparing and releasing a work request"
      >
        {selectedArd &&
          selectedArd?.template !== TemplateType.STANDARD &&
          !isEverySubscriptionProductLinePresent(selectedArd) && (
            <Alert type="warning" header="ARD update required">
              This non-standard ARD has USL Lines with empty subscription
              product line values. Please update the ARD to have valid
              subscription product lines before proceeding.
            </Alert>
          )}
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <h5>
              {index + 1}. {step.title}
            </h5>
            {step.content}
          </React.Fragment>
        ))}
      </ExpandableSection>
    </HelpPanel>
  );
}

const isEverySubscriptionProductLinePresent = (ard: Ard) => {
  return ard?.usecases
    ?.filter((useCase) => useCase.name !== UseCaseName.OTHER)
    .flatMap((useCase) => useCase.uslLines)
    .every((uslLine) => uslLine?.subscriptionProductLine);
};
